/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
/* eslint-disable jsx-a11y/anchor-has-content */
import { FloatingPortal } from '@floating-ui/react'
import { EditPencil, InfoCircle, Plus } from 'iconoir-react'
import { Feature } from 'app/components/auth/Feature'
import FloatingSave from 'app/components/FloatingSave'
import FormStringArrayModal from 'app/components/form/FormStringArrayModal'
import { Info } from 'app/components/Info'
import CustomQuestion from 'app/components/scheduling/CustomQuestion'
import LeadOverridePanel from 'app/components/scheduling/LeadOverridePanel'
import { LocationLogoUploader } from 'app/components/scheduling/LocationLogoUploader'
import QuestionPanel from 'app/components/scheduling/QuestionPanel'
import { SectionCard } from 'app/components/SectionCard'
import { Toggle } from 'app/components/Toggle'
import { INSURANCE_LIST } from 'types/InsuranceList'
import type { UseSchedulingSettingsReturn } from './useSchedulingSettings'

export type SchedulingSettingsComponentProps = UseSchedulingSettingsReturn

export function SchedulingSettingsComponent(props: SchedulingSettingsComponentProps) {
  const {
    schedulingConfiguration,
    setSchedulingConfiguration,
    customQuestions,
    showQuestionPanel,
    setShowQuestionPanel,
    showLeadOverride,
    setShowLeadOverride,
    isDirtyForm,
    setIsDirtyForm,
    locationId,
    saveRequest,
    floatingData,
    getReferenceProps,
    getFloatingProps,
    floatingDataLeadOverride,
    getLeadOverrideReferenceProps,
    getLeadOverrideFloatingProps,
    handleSaveConfiguration,
    onMoveQuestion,
    onDropQuestion,
    onQuestionCreateCompleted,
    onQuestionUpdateCompleted,
    onQuestionDeleted,
    onLeadOverrideSaved,
    onLeadOverrideToggle,
  } = props

  return (
    <>
      <div className="mt-4 flex max-w-[750px] flex-col gap-8">
        {/* General Settings */}
        <SectionCard
          title="General Settings"
          headerButtons={
            <div className="flex items-center gap-2">
              <span className="text-label-sm text-strong">Lead Capture Override</span>
              <Info
                title="New Patient Lead Capture Override"
                message="Prevents a potential patient from seeing the calendar if there is no availability within the designated time. The override will instead show an appointment request form. This is designed to improve the conversion rate and give you the highest chance of capturing a new lead."
                className="text-strong"
              />
              <Toggle
                defaultValue={!!schedulingConfiguration?.leadOverrideEnabled}
                handleToggle={(val) => onLeadOverrideToggle(val)}
              />
            </div>
          }
        >
          <div>
            <p className="mb-2 text-label-sm text-strong">Lead Capture Override</p>
            <p className="mb-4 text-body-sm text-minimal">Replace Calendar with Form if no appointments within...</p>
            <button
              className="flex w-full items-center justify-between rounded-lg border border-default bg-surface px-3 py-2.5 text-strong disabled:bg-surface-strong disabled:text-subtle"
              disabled={!schedulingConfiguration?.leadOverrideEnabled}
              ref={floatingDataLeadOverride.refs.setReference}
              {...getLeadOverrideReferenceProps()}
            >
              <div className="flex items-center gap-2">
                <span className="text-label-sm">{schedulingConfiguration?.leadOverrideConfig?.amount}</span>
                <span className="text-label-sm">{schedulingConfiguration?.leadOverrideConfig?.unit}</span>
              </div>
              <EditPencil height={20} />
            </button>
            {showLeadOverride && (
              <FloatingPortal>
                <div
                  className="rounded-lg border border-default bg-surface shadow-elevation-2"
                  ref={floatingDataLeadOverride.refs.setFloating}
                  {...getLeadOverrideFloatingProps()}
                  style={{ ...floatingDataLeadOverride.floatingStyles }}
                >
                  <LeadOverridePanel
                    config={schedulingConfiguration?.leadOverrideConfig}
                    onSaved={onLeadOverrideSaved}
                    onCancel={() => setShowLeadOverride(false)}
                  />
                </div>
              </FloatingPortal>
            )}
          </div>
        </SectionCard>

        {/* Branding */}
        <SectionCard title="Branding">
          <div>
            <p className="text-ui-md text-strong">Online Scheduling</p>
            <p className="mb-6 mt-2 text-body-sm text-strong">
              This logo will be used in the online scheduler. If no logo is provided, the location name will be shown
              instead.
            </p>
            <LocationLogoUploader locationId={locationId!} />
            <p className="mt-4 text-body-xs text-minimal">Recommended Dimensions: 300x100 pixels</p>
          </div>
        </SectionCard>

        {/* Form Fields */}
        <SectionCard title="Form Fields">
          <div>
            <p className="text-heading-xs text-strong">Required</p>
            <div className="mt-2 grid grid-cols-2 gap-6 rounded-lg border border-default bg-surface p-4">
              <div className="col-span-1 flex flex-col gap-1.5">
                <p className="text-label-sm text-strong">First Name</p>
                <div className="h-10 rounded-lg border border-default bg-surface-subtle"></div>
              </div>
              <div className="col-span-1 flex flex-col gap-1.5">
                <p className="text-label-sm text-strong">Last Name</p>
                <div className="h-10 rounded-lg border border-default bg-surface-subtle"></div>
              </div>
              <div className="col-span-1 flex flex-col gap-1.5">
                <p className="text-label-sm text-strong">Email</p>
                <div className="h-10 rounded-lg border border-default bg-surface-subtle"></div>
              </div>
              <div className="col-span-1 flex flex-col gap-1.5">
                <p className="text-label-sm text-strong">Phone Number</p>
                <div className="h-10 rounded-lg border border-default bg-surface-subtle"></div>
              </div>
            </div>
          </div>
          <div>
            <p className="text-heading-xs text-strong">Custom Questions</p>
            <div className="mt-4 flex flex-col gap-y-4">
              {customQuestions.length ? (
                customQuestions?.map((question, i) => (
                  <CustomQuestion
                    key={question.id}
                    locationId={locationId!}
                    id={question.id}
                    index={i}
                    question={question}
                    isMoveable={!saveRequest.inFlight}
                    moveQuestion={onMoveQuestion}
                    onDrop={onDropQuestion}
                    onQuestionDeleted={(id) => onQuestionDeleted(id)}
                    onQuestionUpdated={(question) => onQuestionUpdateCompleted(question)}
                  />
                ))
              ) : (
                <p className="text-label-xs text-minimal">No custom questions</p>
              )}
              <button
                className="flex w-fit items-center gap-2 rounded-lg border border-default px-6 py-3"
                ref={floatingData.refs.setReference}
                {...getReferenceProps()}
              >
                <Plus height={16} width={16} className="text-strong" />
                <p className="text-label-sm text-strong">Add field</p>
              </button>
              {showQuestionPanel && (
                <FloatingPortal>
                  <div
                    className="rounded-lg border border-default bg-surface shadow-elevation-2"
                    ref={floatingData.refs.setFloating}
                    {...getFloatingProps()}
                    style={{ ...floatingData.floatingStyles }}
                  >
                    <QuestionPanel
                      locationId={locationId!}
                      onSaved={onQuestionCreateCompleted}
                      onCancel={() => setShowQuestionPanel(false)}
                    />
                  </div>
                </FloatingPortal>
              )}
            </div>
          </div>
        </SectionCard>

        {/* Insurances */}
        <Feature name="availability-settings" neg={true}>
          <Feature name="insurances">
            <SectionCard title="Insurances Accepted">
              <div className="border-subtle flex w-fit rounded-md border bg-surface p-4">
                <div className="flex gap-6">
                  <FormStringArrayModal
                    options={INSURANCE_LIST.filter(
                      (insurance) => !schedulingConfiguration?.insurancesRejected?.includes(insurance),
                    )}
                    defaultOptions={schedulingConfiguration?.insurancesAccepted ?? []}
                    title="Insurances In-Network"
                    onChange={(options) => {
                      setSchedulingConfiguration({
                        ...schedulingConfiguration,
                        insurancesAccepted: options,
                      })
                      setIsDirtyForm(true)
                    }}
                  />
                  <FormStringArrayModal
                    options={INSURANCE_LIST.filter(
                      (insurance) => !schedulingConfiguration?.insurancesAccepted?.includes(insurance),
                    )}
                    defaultOptions={schedulingConfiguration?.insurancesRejected ?? []}
                    title="Insurances Rejected"
                    onChange={(options) => {
                      setSchedulingConfiguration({
                        ...schedulingConfiguration,
                        insurancesRejected: options,
                      })
                      setIsDirtyForm(true)
                    }}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="notifyInOutNetwork"
                  className="h-4 w-4 rounded checked:text-default"
                  defaultChecked={schedulingConfiguration?.notifyInOutNetwork}
                  onChange={(e) => {
                    setSchedulingConfiguration({
                      ...schedulingConfiguration,
                      notifyInOutNetwork: e.target.checked,
                    })
                    setIsDirtyForm(true)
                  }}
                />
                <p className="text-body-sm text-strong">Let patients know if they are in network / out of network</p>
              </div>
              <div className="flex items-start gap-3">
                <InfoCircle height={20} width={20} className="text-minimal" />
                <p className="text-body-sm italic text-minimal">
                  By adding an insurance to the &quot;Insurances Rejected&quot; list, Trackable will prevent the ability
                  to schedule if those insurances are selected.
                </p>
              </div>
            </SectionCard>
          </Feature>
        </Feature>
      </div>
      <FloatingSave isDirtyForm={isDirtyForm} handleSaveConfiguration={handleSaveConfiguration} />
    </>
  )
}
